import { exportXLSX } from "./exportXLSX";
import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, TextField, Alert, LinearProgress,
  Button, TableSortLabel, Breadcrumbs, Link, Chip
} from "@mui/material";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const API = process.env.REACT_APP_API_URL || "";
const num = (v) => v != null ? Number(v).toLocaleString("es-MX") : "0";
const headerSx = { fontWeight: 700, bgcolor: "#AF272F", color: "#fff" };
const sortSx = {"& .MuiTableSortLabel-icon":{color:"#fff !important"},color:"#fff !important"};

function exportCSV(rows, columns, labels, filename) {
  const header = labels.join(",");
  const csv = [header, ...rows.map(r => columns.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Default: últimos 30 días
const today = new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

function ReportesAvanceDiario({ token }) {
  // Perfil territorial del usuario
  const perfil = JSON.parse(localStorage.getItem("perfil_territorial") || "null");
  const roleCns = JSON.parse(localStorage.getItem("roles") || "[]");
  const soloEstructura = (roleCns.includes("reportes_estructura") || (perfil && ((perfil.cargo_id >= 3 && perfil.cargo_id <= 6) || perfil.cargo_id === 13))) && !roleCns.includes("compulsa") && !roleCns.includes("monitoreo") && !roleCns.includes("nacional");
  const tipoTerritorio = (soloEstructura && perfil?.afiliacion_tipo) ? perfil.afiliacion_tipo : "NACIONAL";

  const initView = tipoTerritorio === "DISTRITAL" ? "usuario" : tipoTerritorio === "ESTATAL" ? "distrito" : "entidad";
  const initEntidad = (tipoTerritorio === "ESTATAL" || tipoTerritorio === "DISTRITAL") && perfil?.entidad ? { entidad: perfil.entidad } : null;
  const initDistrito = tipoTerritorio === "DISTRITAL" && perfil?.distrito ? { distrito_f: perfil.distrito } : null;

  const [view, setView] = useState(initView);
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedEntidad, setSelectedEntidad] = useState(initEntidad);
  const [selectedDistrito, setSelectedDistrito] = useState(initDistrito);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("afiliaciones");
  const [sortDir, setSortDir] = useState("desc");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const headers = { Authorization: `Bearer ${token}` };

  const buildParams = (extra = {}) => {
    const p = new URLSearchParams();
    if (fechaInicio) p.set("fecha_inicio", fechaInicio);
    if (fechaFin) p.set("fecha_fin", fechaFin);
    Object.entries(extra).forEach(([k, v]) => { if (v != null) p.set(k, v); });
    return p.toString();
  };

  const fetchData = useCallback(async (endpoint, params = "") => {
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/avance-diario/${endpoint}?${params}`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json.data || []);
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }, [token]);

  const fetchChart = useCallback(async (extra = {}) => {
    try {
      const p = buildParams(extra);
      const res = await fetch(`${API}/api/avance-diario/tendencia?${p}`, { headers });
      if (!res.ok) return;
      const json = await res.json();
      setChartData((json.data || []).map(r => ({ ...r, fecha: r.fecha?.slice(5) })));
    } catch {}
  }, [token, fechaInicio, fechaFin]);

  const load = useCallback(() => {
    if (view === "entidad") {
      const extra = {};
      if (tipoTerritorio === "CIRCUNSCRIPCIONAL" && perfil?.circunscripcion) extra.circunscripcion = perfil.circunscripcion;
      else if (tipoTerritorio === "REGIONAL" && perfil?.region_decimal) extra.region_decimal = perfil.region_decimal;
      fetchData("por-entidad", buildParams(extra));
      fetchChart(extra);
    } else if (view === "distrito") {
      fetchData("por-distrito", buildParams({ entidad: selectedEntidad?.entidad }));
      fetchChart({ entidad: selectedEntidad?.entidad });
    } else {
      fetchData("por-usuario", buildParams({ entidad: selectedEntidad?.entidad, distrito_f: selectedDistrito?.distrito_f }));
      fetchChart({ entidad: selectedEntidad?.entidad, distrito_f: selectedDistrito?.distrito_f });
    }
  }, [view, fechaInicio, fechaFin, selectedEntidad, selectedDistrito]);

  useEffect(() => { load(); }, [load]);

  const handleEntidadClick = (row) => { setSelectedEntidad(row); setView("distrito"); setSearch(""); };
  const handleDistritoClick = (row) => { setSelectedDistrito(row); setView("usuario"); setSearch(""); };
  const nivelMinimo = tipoTerritorio === "DISTRITAL" ? "usuario" : tipoTerritorio === "ESTATAL" ? "distrito" : "entidad";
  const handleBack = () => {
    if (view === "usuario" && nivelMinimo !== "usuario") { setView("distrito"); setSelectedDistrito(null); }
    else if (view === "distrito" && nivelMinimo === "entidad") { setView("entidad"); setSelectedEntidad(null); }
    setSearch("");
  };
  const canGoBack = (view === "usuario" && nivelMinimo !== "usuario") || (view === "distrito" && nivelMinimo === "entidad");
  const handleSort = (col) => { if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortBy(col); setSortDir("desc"); } };

  const filtered = data.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.entidad_nombre || "").toLowerCase().includes(s)
      || (r.distrito_f_nombre || "").toLowerCase().includes(s)
      || (r.nombre_completo || "").toLowerCase().includes(s)
      || (r.uid || "").toLowerCase().includes(s);
  }).sort((a, b) => {
    const av = a[sortBy], bv = b[sortBy];
    const an = Number(av), bn = Number(bv);
    const cmp = (!isNaN(an) && !isNaN(bn)) ? an - bn : String(av || "").localeCompare(String(bv || ""), "es");
    return sortDir === "desc" ? -cmp : cmp;
  });

  const totalAfil = data.reduce((s, r) => s + Number(r.afiliaciones || 0), 0);
  const totalVerif = data.reduce((s, r) => s + Number(r.verificados || 0), 0);
  const totalImpr = data.reduce((s, r) => s + Number(r.credenciales_impresas || 0), 0);
  const totalEntr = data.reduce((s, r) => s + Number(r.credenciales_entregadas || 0), 0);

  const title = view === "entidad" ? "Avance Diario — Nacional"
    : view === "distrito" ? `Avance Diario — ${selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "")}`
    : `Avance Diario — ${selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "")} Dto. ${selectedDistrito?.distrito_f}`;

  const cols = view === "entidad"
    ? [{k:"entidad_nombre",l:"Entidad"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"credenciales_impresas",l:"Impresas",a:"right"},{k:"impresas_propias",l:"Imp. Propias",a:"right"},{k:"impresas_terceros",l:"Imp. Terceros",a:"right"},{k:"credenciales_entregadas",l:"Entregadas",a:"right"},{k:"entregadas_propias",l:"Ent. Propias",a:"right"},{k:"entregadas_terceros",l:"Ent. Terceros",a:"right"}]
    : view === "distrito"
    ? [{k:"distrito_f",l:"Dto."},{k:"distrito_f_nombre",l:"Cabecera"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"credenciales_impresas",l:"Impresas",a:"right"},{k:"impresas_propias",l:"Imp. Propias",a:"right"},{k:"impresas_terceros",l:"Imp. Terceros",a:"right"},{k:"credenciales_entregadas",l:"Entregadas",a:"right"},{k:"entregadas_propias",l:"Ent. Propias",a:"right"},{k:"entregadas_terceros",l:"Ent. Terceros",a:"right"}]
    : [{k:"uid",l:"UID"},{k:"nombre_completo",l:"Nombre"},{k:"afiliaciones",l:"Registros",a:"right"},{k:"verificados",l:"Verificados",a:"right"},{k:"rechazados",l:"Rechazados",a:"right"},{k:"credenciales_impresas",l:"Impresas",a:"right"},{k:"impresas_propias",l:"Imp. Propias",a:"right"},{k:"impresas_terceros",l:"Imp. Terceros",a:"right"},{k:"credenciales_entregadas",l:"Entregadas",a:"right"},{k:"entregadas_propias",l:"Ent. Propias",a:"right"},{k:"entregadas_terceros",l:"Ent. Terceros",a:"right"}];

  return (
    <Box sx={{ width: "100%", mx: "auto", px: { xs: 1, sm: 2, md: 3 }, py: 2 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1, flexWrap: "wrap" }}>
        {canGoBack && <IconButton onClick={handleBack} sx={{ color: "#AF272F" }}><ArrowBackIcon /></IconButton>}
        <Typography variant="h5" sx={{ color: "#AF272F", fontWeight: 700, flexGrow: 1 }}>{title}</Typography>
        <IconButton onClick={load} title="Actualizar"><RefreshIcon /></IconButton>
        <Button size="small" startIcon={<DownloadIcon />} onClick={() => exportXLSX(filtered, cols, `avance_diario_${view}_${new Date().toISOString().slice(0,10)}`)} sx={{ color: "#AF272F" }}>Excel</Button>
      </Box>

      {/* Breadcrumbs + Filtros de fecha */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Breadcrumbs>
          {tipoTerritorio === "NACIONAL" && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "entidad" ? 700 : 400, color: view === "entidad" ? "#AF272F" : "inherit" }} onClick={() => { setView("entidad"); setSelectedEntidad(null); setSearch(""); }}>Nacional</Link>}
          {(view === "distrito" || view === "usuario") && <Link underline="hover" sx={{ cursor: "pointer", fontWeight: view === "distrito" ? 700 : 400, color: "#AF272F" }} onClick={() => { setView("distrito"); setSelectedDistrito(null); setSearch(""); }}>{selectedEntidad?.entidad_nombre || (data.length > 0 ? data[0].entidad_nombre : "")}</Link>}
          {view === "usuario" && <Typography color="#AF272F" fontWeight={700}>Dto. {selectedDistrito?.distrito_f}</Typography>}
        </Breadcrumbs>
        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          <TextField size="small" type="date" label="Desde" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          <TextField size="small" type="date" label="Hasta" value={fechaFin} onChange={e => setFechaFin(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
          <Chip label="7d" size="small" onClick={() => { setFechaInicio(daysAgo(7)); setFechaFin(today); }} sx={{ cursor: "pointer" }} />
          <Chip label="30d" size="small" onClick={() => { setFechaInicio(daysAgo(30)); setFechaFin(today); }} sx={{ cursor: "pointer" }} />
          <Chip label="90d" size="small" onClick={() => { setFechaInicio(daysAgo(90)); setFechaFin(today); }} sx={{ cursor: "pointer" }} />
        </Box>
      </Box>

      {/* KPIs */}
      <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #1565c0" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalAfil)}</Typography>
          <Typography variant="caption" color="text.secondary">Registros</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #6a1b9a" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalVerif)}</Typography>
          <Typography variant="caption" color="text.secondary">Verificados</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #e65100" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalImpr)}</Typography>
          <Typography variant="caption" color="text.secondary">Cred. Impresas</Typography>
        </Paper>
        <Paper sx={{ p: 1.5, flex: 1, minWidth: 120, textAlign: "center", borderTop: "3px solid #00695c" }}>
          <Typography variant="h6" fontWeight={700}>{num(totalEntr)}</Typography>
          <Typography variant="caption" color="text.secondary">Cred. Entregadas</Typography>
        </Paper>
      </Box>

      {/* Gráfica de tendencia */}
      {chartData.length > 1 && (
        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            <TrendingUpIcon sx={{ fontSize: 18, verticalAlign: "middle", mr: 0.5 }} /> Tendencia Diaria
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RTooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="afiliaciones" stroke="#1565c0" name="Registros" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="verificados" stroke="#6a1b9a" name="Verificados" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="credenciales_entregadas" stroke="#00695c" name="Entregadas" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Búsqueda */}
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
        <SearchIcon sx={{ color: "text.secondary" }} />
        <TextField size="small" placeholder="Buscar..." value={search} onChange={e => setSearch(e.target.value)} sx={{ width: { xs: "100%", sm: 250, md: 300 } }} />
        <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>{filtered.length} registros</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, "& .MuiLinearProgress-bar": { bgcolor: "#AF272F" } }} />}

      {/* Tabla */}
      <TableContainer component={Paper} sx={{ maxHeight: "50vh", overflowX: "auto", borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {cols.map(c => (
                <TableCell key={c.k} align={c.a || "left"} sx={headerSx}>
                  <TableSortLabel active={sortBy===c.k} direction={sortBy===c.k?sortDir:"asc"} onClick={()=>handleSort(c.k)} sx={sortSx}>{c.l}</TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row, i) => {
              const clickable = view !== "usuario";
              return (
                <TableRow key={i} hover={clickable}
                  onClick={() => { if (view === "entidad") handleEntidadClick(row); else if (view === "distrito") handleDistritoClick(row); }}
                  sx={clickable ? { cursor: "pointer", "&:hover": { bgcolor: "#fce4ec" } } : {}}>
                  {cols.map(c => (
                    <TableCell key={c.k} align={c.a || "left"} sx={c.k === "uid" ? { fontFamily: "monospace", fontSize: "0.8rem" } : {}}>
                      {["afiliaciones","verificados","rechazados","error","credenciales_impresas","credenciales_entregadas","impresas_propias","entregadas_propias","impresas_terceros","entregadas_terceros"].includes(c.k) ? num(row[c.k]) : row[c.k]}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={cols.length} align="center" sx={{ py: 4, color: "text.secondary" }}>Sin datos para el período</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default ReportesAvanceDiario;
